import { useMemo } from 'react';

import { useTabContentConfig } from '../../components/PageConfigProvider/PageConfigProvider';
import { RevelioPageId } from '../../components/PageConfigProvider/types';
import type { MetricValues } from '../../components/PageConfigProvider/types/metric';
import { usePageState } from '../../components/PageState/PageStateProvider';
import { getRevelioPageOverrides } from '../../utils/getRevelioPageOverrides';

// Metrics requiring 'week' granularity
const weeklyMetrics: Partial<Record<MetricValues, boolean>> = {
  posting_salary: true,
  time_to_fill: true,
  postings_active: true,
  postings_new: true,
  postings_removed: true,
  postings_volume: true,
  contractor_pay_rate: true,
  base_salary: true,
  total_compensation: true,
};

export const useViewDateGranularity = (): 'week' | 'month' => {
  const { items, revelioPageId } = useTabContentConfig();
  const { pageState } = usePageState();

  const viewGranularity = useMemo(() => {
    // Posting Discovery lists individual postings (day/week cadence), but its only
    // chart metric (td_users) isn't a weekly metric, so anchor the page to 'week'
    // explicitly. This makes its Post Date chip + form render full dates the same
    // way weekly-metric pages (e.g. Company > Job Postings) do, without a
    // per-filter carve-out.
    if (revelioPageId === RevelioPageId.POSTING_DISCOVERY) {
      return 'week';
    }

    const hasWeeklyMetric = items.some((item) => {
      const { defaultMetric } = getRevelioPageOverrides(item);
      const metric = pageState.metricOverrides?.[item.id] ?? defaultMetric;

      return metric ? Boolean(weeklyMetrics[metric]) : false;
    });

    return hasWeeklyMetric ? 'week' : 'month';
  }, [items, pageState.metricOverrides, revelioPageId]);

  return viewGranularity;
};
